/* Light/dark theme controller. Default = dark; the choice persists per browser
   and is applied to <html data-theme> so theme.css token overrides take effect. */
import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'dark' | 'light';
const KEY = 'flm.adminTheme';

export function getTheme(): ThemeMode {
  const saved = localStorage.getItem(KEY);
  return saved === 'light' ? 'light' : 'dark';
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', mode);
}

/** Hook: current theme + a toggle that persists and re-applies. */
export function useTheme() {
  const [theme, setThemeState] = useState<ThemeMode>(getTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    localStorage.setItem(KEY, mode);
    setThemeState(mode);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(KEY, next);
      return next;
    });
  }, []);

  return { theme, setTheme, toggle };
}
